import type { API } from 'chessboard/api/config/types';
import { Core } from 'chessboard/core/config/types';
import { extendMove } from 'chessboard/model/move/extend-move';
import type { Game } from 'chessboard/model/game/types';
import { formatAPIMove } from 'api/move/model/format-api-move';
import { handleTimestamp } from 'chessboard/api/move/model/handle-timestamp';
import type JCE from '@chesscom/js-chess-engine';

/**
 * Create the createMove method.
 */
export function createMove(context: API.Context) {
  /**
   * Make a move.
   */
  return function move(
    mv: API.Options.Move | string,
    followContinuations: boolean = true,
  ): API.Response {
    const { api, game, mutateGame, respond } = context;

    const formattedMove = formatAPIMove(mv, api.getFEN(), api.getVariant());

    const data =
      formattedMove !== null
        ? mutateGame(function makeMove(): void | null {
            let gameMove: boolean | JCE.Move.Model | null;

            if ('san' in formattedMove) {
              // san can be an empty string
              gameMove = game.move(formattedMove.san || formattedMove, followContinuations);
            } else if (typeof mv === 'object' && 'tcn' in mv) {
              gameMove = game.loadMoves(mv.tcn.slice(0, 2), { atCurrent: true, method: 'merge' });
            } else {
              gameMove = game.move(formattedMove, followContinuations);
            }
            if (!gameMove) {
              return null;
            }

            if (formattedMove.time) {
              handleTimestamp({ context, time: formattedMove.time });
            }
          })
        : null;

    if (data === null) {
      return respond(Core.APIEvents.IllegalMove, { fen: game.fen(), move: mv });
    }

    if (data.result) {
      api.setResult(data.result);
    }

    if (data.move && formattedMove) {
      Object.assign(data.move, {
        animate: formattedMove.animate,
        userGenerated: formattedMove.userGenerated,
        userGeneratedDrop: formattedMove.userGeneratedDrop,
        isExternalBoard: formattedMove.isExternalBoard,
      });

      if (formattedMove.ac) {
        // AC is an obfuscation of "Anti-Cheat"
        data.move.ac = formattedMove.ac;
      }

      data.move = extendMove(<Game.Move.ExtendedNode>data.move);
    }

    return respond(Core.APIEvents.Move, data);
  };
}
